import isEmpty from "lodash/isEmpty";
import isNull from "lodash/isNull";
import { shallow } from "zustand/shallow";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

import useStripePaymentMethodStore, {
  STRIPE_PAYMENT_METHODS_QUERY_KEYS,
} from "../store";
import { getActiveSubscription } from "../../../api/stripe";
import { bugNotif } from "../../../api/bugsnag";
import { getErrorMsg } from "../../../../Common/utils/common";
import { WLStripeWPlanSubscription } from "../../../../Common/models/subscriptions";
import { useWebUserStore } from "../../../CommonStores/user/users";

const useExistingStripeSubscriptionProvider = () => {
  const {
    setExistingSubscription,
    setExistingSubscriptionLoading,
    setExistingSubscriptionLoadFailed,
  } = useStripePaymentMethodStore((state) => state, shallow);
  const [user] = useWebUserStore((state) => [state.user]);

  const {
    data: existingSubscriptionResult,
    error: existingSubscriptionError,
    isPending: existingSubscriptionPending,
  } = useQuery({
    queryKey: [STRIPE_PAYMENT_METHODS_QUERY_KEYS.existingSubscription, user],
    queryFn: async () => {
      if (
        user === undefined ||
        isNull(user) ||
        user.stripeId === undefined ||
        isEmpty(user.stripeId)
      ) {
        return undefined;
      }
      return await getActiveSubscription(user.stripeId);
    },
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    setExistingSubscriptionLoading(existingSubscriptionPending);
  }, [existingSubscriptionPending, setExistingSubscriptionLoading]);

  useEffect(() => {
    if (isNull(existingSubscriptionError)) {
      setExistingSubscriptionLoadFailed(false);
      return;
    }

    bugNotif(
      STRIPE_PAYMENT_METHODS_QUERY_KEYS.existingSubscription,
      getErrorMsg(existingSubscriptionError).rawErrorMsg
    );
    // Keep the last known subscription: `null` means "Stripe confirmed no subscription",
    // which downstream CTAs and the no-membership banner rely on.
    setExistingSubscriptionLoadFailed(true);
  }, [existingSubscriptionError, setExistingSubscriptionLoadFailed]);

  useEffect(() => {
    if (existingSubscriptionResult !== undefined) {
      const existingSubscription = existingSubscriptionResult.data
        .result as WLStripeWPlanSubscription | null;
      setExistingSubscription(existingSubscription);
      //! handle pending error somewhere else
    }
  }, [existingSubscriptionResult]);
};

export default useExistingStripeSubscriptionProvider;
